import { useCallback, useEffect, useMemo, useState } from "react";

import { faArrowLeft, faCheck } from "@fortawesome/pro-regular-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Label } from "src/base-components/Label";
import { mdService } from "src/base-components/TextEditor/MarkdownService";
import { FormField } from "src/cases/caseView/components/FormField";
import {
  useAvailableCaseTasks,
  useCaseTaskFormSchema,
  useGetCaseTemplateTask,
  useTaskFormValidForCompletion,
} from "src/cases/caseView/hooks";
import {
  useSaveCaseTaskForm,
  useUpdateCaseTaskCompletion,
} from "src/cases/queries/cases";
import {
  CaseDeserialized,
  type CaseTask,
  TaskIdExtractor,
  transformCaseTask,
} from "src/cases/queries/casesTypes";
import { useTaskFieldHints } from "src/cases/queries/hints";
import { CaseTemplateTaskOutput } from "src/clients/case-management";
import { IconButton } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";

type TaskFormSidebarViewProps = {
  task: CaseTask;
  templateTask: CaseTemplateTaskOutput;
  caseData?: CaseDeserialized;
  disabled?: boolean;
  onBack: () => void;
};

export const TaskFormSidebarView: React.FC<TaskFormSidebarViewProps> = ({
  task: initialTask,
  templateTask: fallbackTemplate,
  caseData,
  disabled,
  onBack,
}) => {
  const caseId = caseData?.data.id;
  const availableTasks = useAvailableCaseTasks(caseData);
  const getTemplate = useGetCaseTemplateTask(caseData);

  const task = useMemo(() => {
    const updatedTask = availableTasks.find((t) => t.id === initialTask.id);
    const transformed = updatedTask
      ? transformCaseTask(updatedTask, getTemplate)
      : initialTask;

    if (
      JSON.stringify(transformed.formValues) ===
        JSON.stringify(initialTask.formValues) &&
      transformed.isCompleted === initialTask.isCompleted
    ) {
      return initialTask;
    }
    return transformed;
  }, [availableTasks, getTemplate, initialTask]);

  const template = getTemplate(task.key) || fallbackTemplate;

  const { data: fieldHints } = useTaskFieldHints(caseId, task.id);

  const schema = useCaseTaskFormSchema(template);
  const { mutateAsync: saveTask, isPending } = useSaveCaseTaskForm(caseId);
  const { mutateAsync: updateTaskCompletion, isPending: isCompletionPending } =
    useUpdateCaseTaskCompletion(caseId);

  const defaultValues = useMemo(() => {
    const fields: Record<string, unknown> = {};

    template?.form?.fields?.forEach((field) => {
      fields[field.key] = field.type === "multi_select" ? [] : null;
    });

    return { ...fields, ...task.formValues, ...(fieldHints ?? {}) };
  }, [template?.form?.fields, task.formValues, fieldHints]);

  const form = useForm<Record<string, unknown>>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { isDirty, isValid } = form.formState;

  const currentFormValues = form.watch();
  const canCompleteTask = useTaskFormValidForCompletion(
    template,
    currentFormValues as Record<string, unknown>,
  );

  const isFormEmpty = useMemo(() => {
    return (
      template?.form?.fields?.every((field) => {
        const value = defaultValues[field.key];
        return field.type === "multi_select"
          ? Array.isArray(value) && value.length === 0
          : value == null;
      }) ?? true
    );
  }, [defaultValues, template?.form?.fields]);

  useEffect(() => {
    form.reset(defaultValues, { keepDirty: false, keepTouched: false });
  }, [defaultValues, form]);

  const saveForm = useCallback(async () => {
    if (!task.formMeta?.id) {
      logger.warn(`Task form ID is missing for task ${task.key}`);
      return;
    }

    const currentValues = form.getValues();
    form.reset(currentValues, { keepValues: true });

    try {
      const validatedValues = schema.cast(currentValues, {
        stripUnknown: true,
      }) as Record<string, unknown>;

      await saveTask({
        taskKey: TaskIdExtractor.getTaskKey(task.id),
        fullTaskId: task.id,
        formEtag: task.formMeta?.etag ?? 0,
        taskFormId: task.formMeta.id,
        taskFormValues: validatedValues,
      });
    } catch (error) {
      form.reset(currentValues, { keepValues: true, keepDirty: true });
      logger.error("Error saving case task form:", error);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Failed to save task form",
          description:
            "The task has been modified elsewhere. Please reload and try again.",
        });
      } else if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to save task form",
          description: error.response?.data?.message,
        });
      } else {
        toastActions.failure({
          title: "Failed to save task form",
          description: "An unexpected error occurred. Please try again.",
        });
      }
      throw error;
    }
  }, [form, task, schema, saveTask]);

  const handleCompleteTask = useCallback(async () => {
    if (!caseData) {
      logger.error("Case data is missing");
      return;
    }

    if (!task.isCompleted && isDirty) {
      try {
        await saveForm();
      } catch {
        // saveForm already handles error toasts
        return;
      }
    }

    try {
      await updateTaskCompletion({
        taskKey: task.key,
        taskId: task.id,
        isCompleted: !task.isCompleted,
        etag: caseData.data.etag,
      });

      toastActions.success({
        title: task.isCompleted
          ? "Task marked as incomplete"
          : "Task completed",
      });
    } catch (error) {
      logger.error("Error updating task completion:", error);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Failed to update task",
          description:
            "The task or case has been modified elsewhere. Please reload and try again.",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to update task",
          description: error.response?.data?.message,
        });
        return;
      }

      toastActions.failure({
        title: "Failed to update task",
        description: "An unexpected error occurred. Please try again.",
      });
    }
  }, [task, caseData, updateTaskCompletion, isDirty, saveForm]);

  const handleClearForm = useCallback(async () => {
    const emptyValues: Record<string, unknown> = {};
    template?.form?.fields?.forEach((field) => {
      emptyValues[field.key] = field.type === "multi_select" ? [] : null;
    });

    form.reset(emptyValues, { keepDirty: true });

    try {
      await saveForm();
    } catch {
      // saveForm already handles error toasts
    }
  }, [form, template?.form?.fields, saveForm]);

  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState(false);

  const handleBack = useCallback(() => {
    if (isDirty) {
      setIsUnsavedModalOpen(true);
    } else {
      onBack();
    }
  }, [isDirty, onBack]);

  const handleSaveAndGoBack = useCallback(async () => {
    try {
      await saveForm();
      setIsUnsavedModalOpen(false);
      onBack();
    } catch {
      // saveForm already shows error toasts — keep modal open so user can retry or keep editing
    }
  }, [saveForm, onBack]);

  if (!template.form?.fields?.length) {
    return null;
  }

  const checkboxDisabled = disabled || (!task.isCompleted && !canCompleteTask);
  const canSave = !isPending && isDirty && isValid;

  return (
    <div className="flex h-full flex-col" data-loc="task-form-sidebar">
      <div className="flex shrink-0 flex-col gap-y-3 p-4 pb-0">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <div className="shrink-0">
              <IconButton
                dataLoc="task-form-sidebar-back"
                icon={faArrowLeft}
                size="xs"
                onClick={handleBack}
              />
            </div>

            <p className="truncate font-inter-semibold-13px text-gray-800">
              {task.title}
            </p>
          </div>
          {task.description && (
            <div
              //skipcq: JS-0440
              dangerouslySetInnerHTML={{
                __html: mdService.toHtml(task.description),
              }}
              className="tktl-markdown font-inter-normal-12px text-gray-500"
            />
          )}
        </div>
        <div className="border-gray-200 border-b" />
      </div>

      <div className="decideScrollbar min-h-0 flex-1 overflow-auto">
        <div className="p-4">
          <FormProvider {...form}>
            <form className="flex flex-col gap-y-3">
              {template.form.fields.map((field) => (
                <div key={field.key}>
                  <Label
                    mb={field.description ? "mb-0" : "mb-2"}
                    required={field.required}
                  >
                    {field.display_name}
                  </Label>
                  {field.description && (
                    <div
                      //skipcq: JS-0440
                      dangerouslySetInnerHTML={{
                        __html: mdService.toHtml(field.description),
                      }}
                      className="tktl-markdown mb-2 font-inter-normal-12px text-gray-500"
                    />
                  )}
                  <FormField
                    disabled={disabled}
                    field={field}
                    hinted={fieldHints != null && field.key in fieldHints}
                  />
                  {form.formState.errors?.[field.key] && (
                    <ErrorHint>
                      {form.formState.errors[field.key]?.message}
                    </ErrorHint>
                  )}
                </div>
              ))}
            </form>
          </FormProvider>
        </div>

        <div className="sticky bottom-0 border-gray-200 border-t bg-white px-5 py-4">
          <div className="flex items-center justify-between">
            <Button
              dataLoc="clear-form"
              disabled={isPending || (isFormEmpty && !isDirty)}
              variant="secondary"
              onClick={handleClearForm}
            >
              Clear form
            </Button>
            <div className="flex gap-3">
              <Button
                dataLoc="save-form"
                disabled={!canSave}
                loading={isPending}
                variant="secondary"
                onClick={saveForm}
              >
                Save
              </Button>
              <Button
                dataLoc="complete-task"
                disabled={
                  checkboxDisabled ||
                  isCompletionPending ||
                  (isDirty && isPending)
                }
                iconLeft={task.isCompleted ? undefined : faCheck}
                variant={task.isCompleted ? "secondary" : "primary"}
                onClick={handleCompleteTask}
              >
                {task.isCompleted ? "Mark incomplete" : "Complete task"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        cancelationButtonText="Keep editing"
        confirmationButtonText="Save & Go back"
        dataLoc="unsaved-changes-modal"
        description="You've made updates to this task form. If you leave now, your changes won't be saved."
        open={isUnsavedModalOpen}
        title="You have unsaved changes"
        onClose={() => setIsUnsavedModalOpen(false)}
        onConfirm={handleSaveAndGoBack}
      />
    </div>
  );
};
